import { useImperativeHandle, useRef, useState, type Ref } from 'react';
import toast from 'react-hot-toast';
import { useRemoveCartItem, useSetCartItemBuyCode, useUpdateCart } from '~/api';
import { wholeBundlePieces } from '~/components/ui/bundleQuantity';
import type { Quantity, QuantityInput, UnitCode } from '~/graphql/graphql';
import { useDebounce } from '~/hooks/useDebounce';
import { useSubmitVehicleCapacity } from '~/hooks/useSubmitVehicleCapacity';
import { useOptimisticCartQuantity } from '~/hooks/useOptimisticCartQuantity';
import { extractGraphQLErrorMessages, type ClientError } from '~/utils';
import {
  buildCartItemUpdateInput,
  computeExactPieces,
  type CartOrderItem,
} from '~/utils/order';
import { toStandardUnits, type Dimensions } from '~/utils/unitConversion';

/**
 * Lets a parent force a pending quantity update to the server before navigating
 * away. Resolves false when that update was rejected, so callers can hold the
 * navigation back; true when it saved or when nothing was pending.
 */
export interface CartListItemHandle {
  flush: () => Promise<boolean>;
}

type UseCartItemMutationsArgs = {
  item: CartOrderItem;
  cartId: string;
  locationId: string;
  sellingOrganizationFacilityId: string;
  parentOrderId?: string | null;
  selectedTransportVehicleId: string;
  suggestedPriceInStorageUnit: string;
  priceUnit: UnitCode;
  uppPrice: number | null;
  bundleSize: number;
  dims: Dimensions;
  handleRef: Ref<CartListItemHandle>;
};

/**
 * Every cart-API call this line item makes, plus the optimistic quantity
 * bookkeeping that has to be undone when the server rejects an update.
 */
export function useCartItemMutations({
  item,
  cartId,
  locationId,
  sellingOrganizationFacilityId,
  parentOrderId,
  selectedTransportVehicleId,
  suggestedPriceInStorageUnit,
  priceUnit,
  uppPrice,
  bundleSize,
  dims,
  handleRef,
}: UseCartItemMutationsArgs) {
  const [deletingCart, setDeletingCart] = useState(false);
  const {
    applyQuantityOverride,
    settleQuantityOverride,
    clearQuantityOverride,
  } = useOptimisticCartQuantity();
  const editVersionRef = useRef(0);
  const pendingBundleSaveRef = useRef<Promise<boolean> | null>(null);

  const { mutateAsync: updateCartAsync } = useUpdateCart();
  const { mutate: removeCartItem } = useRemoveCartItem();
  const submitVehicleCapacity = useSubmitVehicleCapacity();
  const { mutateAsync: setCartItemBuyCodeAsync } = useSetCartItemBuyCode();

  const sendCartUpdate = (
    productId: string,
    quantity: QuantityInput,
    priceSuggestion?: string | null
  ) => {
    let parsedSuggestedPrice: number | null | undefined;
    if (priceSuggestion === null || priceSuggestion === '')
      parsedSuggestedPrice = null;
    else if (priceSuggestion)
      parsedSuggestedPrice = parseFloat(priceSuggestion);

    return updateCartAsync({
      ...buildCartItemUpdateInput({
        productId,
        locationId,
        facilityId: sellingOrganizationFacilityId,
        quantity: quantity.value,
        unit: quantity.unit,
        parentOrderId,
      }),
      suggestedPrice: parsedSuggestedPrice,
    });
  };

  /**
   * The quantity path, and the only one that rolls back: dropping the edit is
   * the rollback, because the server value it falls back to was never
   * overwritten.
   *
   * Both outcomes are ignored once a later edit has been queued. A request still
   * in flight when the buyer types again is answering for a quantity they have
   * already replaced: rolling back on its rejection would discard the newer edit
   * (and report an error about a figure that is no longer on screen), and
   * settling on its success would let the next server payload drop that edit.
   */
  const saveQuantity = async (
    productId: string,
    quantity: QuantityInput,
    priceSuggestion: string | null | undefined,
    editVersion: number
  ) => {
    const isLatestEdit = () => editVersion === editVersionRef.current;
    try {
      await sendCartUpdate(productId, quantity, priceSuggestion);
      if (isLatestEdit()) settleQuantityOverride(cartId, productId);
      return true;
    } catch (error) {
      if (!isLatestEdit()) return false;
      clearQuantityOverride(cartId, productId);
      toast.error(extractGraphQLErrorMessages(error as ClientError).join(', '));
      return false;
    }
  };

  /**
   * A price change says nothing about whether a pending quantity edit would
   * save, so a failure here reports itself and leaves the quantity alone.
   */
  const savePriceSuggestion = async (
    productId: string,
    quantity: QuantityInput,
    priceSuggestion?: string | null
  ) => {
    try {
      await sendCartUpdate(productId, quantity, priceSuggestion);
      return true;
    } catch (error) {
      toast.error(extractGraphQLErrorMessages(error as ClientError).join(', '));
      return false;
    }
  };

  const saveBuyCode = async (productId: string, buyCode: string) => {
    const trimmed = buyCode.trim();
    try {
      await setCartItemBuyCodeAsync({
        cartId,
        platformProductLengthId: productId,
        buyCode: trimmed === '' ? null : trimmed,
      });
      return true;
    } catch (error) {
      toast.error(extractGraphQLErrorMessages(error as ClientError).join(', '));
      return false;
    }
  };

  const debouncedAddToCart = useDebounce(saveQuantity, 3000);
  const debouncedSaveBuyCode = useDebounce(saveBuyCode, 3000);

  const onUpdateBuyCode = (productId: string, buyCode: string) => {
    debouncedSaveBuyCode(productId, buyCode);
  };

  const flushBuyCode = () => {
    void debouncedSaveBuyCode.flush();
  };

  useImperativeHandle(
    handleRef,
    () => ({
      flush: async () => {
        const quantityFlush = debouncedAddToCart.flush();
        const buyCodeFlush = debouncedSaveBuyCode.flush();
        const bundleSave = pendingBundleSaveRef.current;
        const quantityOk = (await quantityFlush) ?? true;
        const buyCodeOk = (await buyCodeFlush) ?? true;
        const bundleOk = (await bundleSave) ?? true;
        return quantityOk && buyCodeOk && bundleOk;
      },
    }),
    [debouncedAddToCart, debouncedSaveBuyCode]
  );

  const onUpdateQuantity = (productId: string, newQuantity: number) => {
    // Send the exact piece count to avoid floating-point CEILING errors — see
    // computeExactPieces for why the bundle count is recovered first rather
    // than dividing straight through by unitsPerPiece. exactPieces also
    // optimistically updates quantityStored below — calculateOrderTotal
    // prefers pricePerPiece × quantityStored over effectivePrice ×
    // quantity.value, so without it the on-screen total stays stale until the
    // debounced server update round-trips back.
    let sendQuantity: QuantityInput;
    let exactPieces = computeExactPieces(
      newQuantity,
      item.effectivePiecesPerBundle,
      bundleSize
    );
    if (exactPieces != null) {
      sendQuantity = { value: exactPieces, unit: 'PCS' };
    } else {
      const sendValue = toStandardUnits(
        newQuantity,
        item.quantity.unit,
        priceUnit,
        dims.lengthInches,
        dims.widthInches,
        dims.thicknessInches
      );
      sendQuantity = { value: sendValue, unit: priceUnit };
      if (uppPrice != null && uppPrice > 0) {
        exactPieces = Math.round(sendValue / uppPrice);
      }
    }

    applyQuantityOverride(cartId, productId, newQuantity, exactPieces);

    editVersionRef.current += 1;
    debouncedAddToCart(
      productId,
      sendQuantity,
      suggestedPriceInStorageUnit,
      editVersionRef.current
    );
  };

  const onUpdatePiecesPerBundle = (
    productId: string,
    newPiecesPerBundle: number,
    quantityKeepingBundleCount: Quantity
  ) => {
    debouncedAddToCart.cancel();
    editVersionRef.current += 1;
    const editVersion = editVersionRef.current;
    const isLatestEdit = () => editVersion === editVersionRef.current;
    const sendQuantity = wholeBundlePieces(
      quantityKeepingBundleCount,
      newPiecesPerBundle,
      item.platformProductsLengths
    );

    const saveBundle = async () => {
      try {
        await updateCartAsync({
          ...buildCartItemUpdateInput({
            productId,
            locationId,
            facilityId: sellingOrganizationFacilityId,
            quantity: sendQuantity.value,
            unit: sendQuantity.unit,
            parentOrderId,
            piecesPerBundle: newPiecesPerBundle,
          }),
          suggestedPrice: undefined,
        });
        if (isLatestEdit()) {
          settleQuantityOverride(cartId, productId);
        }
        return true;
      } catch (error) {
        if (isLatestEdit()) {
          clearQuantityOverride(cartId, productId);
        }
        toast.error(
          extractGraphQLErrorMessages(error as ClientError).join(', ')
        );
        return false;
      }
    };
    const bundleSave = saveBundle().finally(() => {
      if (pendingBundleSaveRef.current === bundleSave) {
        pendingBundleSaveRef.current = null;
      }
    });
    pendingBundleSaveRef.current = bundleSave;
  };

  const onRemoveItem = (productId: string) => {
    debouncedAddToCart.cancel();
    debouncedSaveBuyCode.cancel();
    editVersionRef.current += 1;
    clearQuantityOverride(cartId, productId);
    setDeletingCart(true);
    removeCartItem(
      { cartId, platformProductLengthId: productId },
      {
        onError: (error) =>
          toast.error(extractGraphQLErrorMessages(error).join(', ')),
        onSettled: () => setDeletingCart(false),
      }
    );
  };

  const onCapacitySubmit = (value: number) =>
    submitVehicleCapacity({
      platformProductLengthId: item.platformProductsLengths.id,
      transportVehicleId: selectedTransportVehicleId,
      value,
      unit: item.quantity.unit,
    });

  return {
    deletingCart,
    savePriceSuggestion,
    onUpdateQuantity,
    onUpdateBuyCode,
    flushBuyCode,
    onUpdatePiecesPerBundle,
    onRemoveItem,
    onCapacitySubmit,
  };
}
